import { X } from 'lucide-react';

/**
 * Documentación de la sección Modificación de datos: el flujo de punta a punta,
 * qué se puede cambiar en cada flujo y por qué una fila no se procesó.
 *
 * Mismo criterio que `AyudaBajasModal`: se abre sola la primera vez que alguien
 * entra a la sección (ver `useVistoUnaVez`) y después queda en el signo de
 * pregunta del header.
 *
 * Escrita desde el lado del operador: qué ve y qué tiene que hacer. Los nombres
 * técnicos de las clasificaciones no aparecen — el operador ve el texto de la
 * pantalla, no `rechazada_transicion`.
 */
export function AyudaModificacionesModal({ onClose }: { onClose: () => void }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Cómo funciona la modificación de datos"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,37,100,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 50,
      }}
    >
      <div
        className="card"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(760px, 100%)',
          maxHeight: '88vh',
          display: 'flex',
          flexDirection: 'column',
          gap: 0,
          padding: 0,
        }}
      >
        <header
          className="rowBetween"
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid var(--color-outline)',
            alignItems: 'center',
          }}
        >
          <h2 className="heading-5" style={{ margin: 0 }}>
            Cómo funciona la modificación de datos
          </h2>
          <button type="button" className="btn btn-ghost btn--sm" onClick={onClose}>
            <X size={18} />
          </button>
        </header>

        <div style={{ overflow: 'auto', padding: '18px 20px 22px', display: 'grid', gap: 22 }}>
          <p className="text-element-mid" style={{ margin: 0, fontSize: 14.5, lineHeight: 1.55 }}>
            Acá se modifican los datos de personas que <strong>ya están dadas de alta</strong>.
            Cargás un archivo con los valores nuevos y el sistema los revisa uno por uno contra
            Binary <strong>en vivo</strong>: compara cada valor con el que la persona tiene hoy,
            descarta los que ya están iguales y frena los cambios que no están permitidos. Los
            datos de identidad —apellido, nombres y fecha de nacimiento— pasan además por
            <strong> RENAPER</strong>, que es quien decide si el valor nuevo es el correcto.
            Después vas a ver el resultado de cada fila, con el motivo, antes de confirmar nada.
          </p>

          <Seccion titulo="El flujo, paso a paso">
            <Paso n={1} titulo="Elegir el flujo">
              Arriba hay dos solapas. <b>Datos Sensibles</b> son los que tocan la identidad:
              apellido, nombres, fecha de nacimiento, documento, CUIL y parentesco.{' '}
              <b>Datos No Sensibles</b> son mail, teléfono y estado civil. Cada uno tiene su propio
              permiso de carga: podés ver la sección sin poder subir archivos.
            </Paso>
            <Paso n={2} titulo="Descargar la plantilla">
              Trae las columnas del flujo que elegiste, los valores permitidos y qué queda fuera de
              alcance. <b>Dejá vacía toda columna que no quieras cambiar</b>: una celda vacía se
              ignora, no borra el dato.
            </Paso>
            <Paso n={3} titulo="Subir el archivo">
              La primera columna es el <b>CUIL</b> y es con lo que se busca a la persona. No se
              modifica nada todavía: se valida todo y se muestra el resultado.
            </Paso>
            <Paso n={4} titulo="Revisar">
              Cada modificación queda con un estado y su motivo. Las que tienen error se excluyen
              solas; podés tildar alguna de las válidas si además querés dejarla afuera.
            </Paso>
            <Paso n={5} titulo="Confirmar">
              Lo que quedó se manda a Binary. La pantalla sigue el envío sola y te muestra qué
              impactó y qué no, con el número de solicitud de Binary en cada fila.
            </Paso>
          </Seccion>

          <Seccion titulo="Qué significa cada resultado">
            <Fila
              etiqueta="Lista para enviar"
              texto="Pasó todas las validaciones. Se va a mandar a Binary al confirmar."
            />
            <Fila
              etiqueta="Sin cambio"
              texto="El valor que mandaste es el que la persona ya tiene. No se manda nada: repetir un archivo no vuelve a impactar."
            />
            <Fila
              etiqueta="Solo consulta"
              texto="La columna SEXO no modifica nada. Viaja para poder consultar RENAPER, que la necesita junto con el documento."
            />
            <Fila
              etiqueta="Con error"
              texto="El valor tiene un problema de formato, el cambio no está permitido, la persona no aparece en el padrón o no está en un estado que se pueda modificar. El motivo puntual está en la fila."
            />
            <Fila
              etiqueta="No se pudo validar"
              texto="No es que el dato esté mal: no se pudo consultar Binary o RENAPER en ese momento. Volvé a subir el archivo más tarde."
            />
            <Fila
              etiqueta="Impactó / No impactó"
              texto="Aparecen después de confirmar. Lo que no impactó trae el motivo que devolvió Binary; corregilo y volvé a subirlo — lo que ya se aplicó no se manda de nuevo."
            />
          </Seccion>

          <Seccion titulo="Cosas que conviene saber antes">
            <Fila
              etiqueta="Apellido, nombres y fecha de nacimiento los arbitra RENAPER"
              texto="No alcanza con que el formato esté bien: RENAPER tiene que confirmar el valor. Si no coincide, la pantalla te muestra qué dice RENAPER para que puedas corregir."
            />
            <Fila
              etiqueta="El parentesco cambia el grupo familiar entero"
              texto="Binary reemplaza la foto completa del grupo, así que se manda a todos los integrantes. Un legajo no puede quedar con dos cónyuges: si el cambio llevaría a eso, se frena y se avisa."
            />
            <Fila
              etiqueta="Mail y teléfono son solo del titular"
              texto="Si vienen en la fila de un integrante se rechazan con ese motivo."
            />
            <Fila
              etiqueta="Nada se descarta en silencio"
              texto="Toda fila del archivo aparece en el resultado con su estado, incluidas las repetidas y las que traen columnas que no corresponden a este flujo."
            />
            <Fila
              etiqueta="La planilla para Administración sigue estando"
              texto="Después de confirmar podés descargarla para cargar a mano lo que Binary haya rechazado."
            />
          </Seccion>
        </div>

        <footer
          style={{
            padding: '12px 20px',
            borderTop: '1px solid var(--color-outline)',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Entendido
          </button>
        </footer>
      </div>
    </div>
  );
}

function Seccion({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section style={{ display: 'grid', gap: 10 }}>
      <h3
        style={{
          margin: 0,
          fontSize: 12,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--color-element-mid)',
        }}
      >
        {titulo}
      </h3>
      {children}
    </section>
  );
}

function Paso({ n, titulo, children }: { n: number; titulo: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      <span
        aria-hidden
        style={{
          flex: '0 0 auto',
          width: 22,
          height: 22,
          borderRadius: '50%',
          background: 'var(--color-primary)',
          color: '#fff',
          fontSize: 12,
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginTop: 1,
        }}
      >
        {n}
      </span>
      <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.5 }}>
        <strong>{titulo}.</strong> {children}
      </p>
    </div>
  );
}

function Fila({ etiqueta, texto }: { etiqueta: string; texto: string }) {
  return (
    <div style={{ display: 'grid', gap: 2 }}>
      <span style={{ fontSize: 13.5, fontWeight: 650 }}>{etiqueta}</span>
      <span className="text-element-mid" style={{ fontSize: 13.5, lineHeight: 1.45 }}>
        {texto}
      </span>
    </div>
  );
}
